// Khai Bảo (ITEMS §9, UI_SPEC §12 biến thể bậc ⑤): màn tối 0,4s → rương mực mở 0,6s → món xoay một
// vòng trong cột sáng → dòng Tiền Chủ gõ 18 ký tự/s → nút NHẬN. Bấm để tua; lần đầu gặp độc bản thì
// không bỏ qua dòng Tiền Chủ. Lớp DOM còn lại tắt (opacity 0) trong lúc diễn; tranh vẽ ở Pixi.
import type { JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '@i18n/t';
import { RARITIES } from '@data/items/types';
import { uniqueDef } from '@data/items/uniques';
import { finishReveal, revealing } from '@state/modules/reveal';
import { isClaimed, markClaimed, ledger } from '@state/modules/ledger';
import { settings } from '@state/modules/settings';
import { TextButton } from '../kit/TextButton';
import { bell, haptic } from '../audio';
import { itemArt, itemName, rarityClass } from '../itemView';

export interface RiteArt {
  start(art: string | null, lv: number, reduced?: boolean): void;
  skip(): void;
  stop(): void;
  readonly phase: number;
}
let art: RiteArt | null = null;
export function setRiteArt(a: RiteArt): void {
  art = a;
}

const CHARS_PER_S = 18;

function ownerText(uniqueId: string | undefined): string {
  if (!uniqueId) return '';
  const u = uniqueDef(uniqueId);
  for (const v of u.formerOwnerVariants ?? []) {
    const [flag, val] = v.when.split('=') as [string, string];
    if (String(ledger.peek().flags[flag]) === val) return t(`${uniqueId}.formerOwner.${v.suffix}`);
  }
  return t(`${uniqueId}.formerOwner`);
}

export function RevealHost(): JSX.Element | null {
  const it = revealing.value;
  const [shown, setShown] = useState(0);
  const [ready, setReady] = useState(false);
  const firstTime = useRef(false);
  const text = it ? ownerText(it.uniqueId) : '';

  useEffect(() => {
    if (!it) return;
    const lv = RARITIES.indexOf(it.rarity);
    firstTime.current = !!it.uniqueId && !isClaimed(`reveal.${it.uniqueId}`);
    document.documentElement.classList.add('rite-on');
    const rm = settings.peek().reduceMotion;
    art?.start(itemArt(it), lv, rm);
    bell(lv);
    haptic(5);
    setShown(0);
    setReady(false);
    const t0 = performance.now() + (rm ? 0 : 2000);
    const id = setInterval(() => {
      const el = (performance.now() - t0) / 1000;
      if (el < 0) return;
      const n = Math.min(text.length, Math.floor(el * CHARS_PER_S));
      setShown(n);
      if (n >= text.length) {
        setReady(true);
        clearInterval(id);
      }
    }, 50);
    return () => {
      clearInterval(id);
      document.documentElement.classList.remove('rite-on');
      art?.stop();
    };
  }, [it?.uid]);

  if (!it) return null;
  const q = rarityClass(it.rarity);
  const tap = (): void => {
    art?.skip();
    // Lần đầu gặp độc bản: không tua dòng Tiền Chủ.
    if (!firstTime.current) {
      setShown(text.length);
      setReady(true);
    }
  };
  return (
    <section
      class="ht-reveal"
      data-id="reveal"
      role="dialog"
      aria-modal="true"
      aria-label={t('item.revealRite')}
      onClick={tap}
    >
      <b class={`reveal-name ${q}`}>{itemName(it)}</b>
      {text && (
        <p class="reveal-owner" data-id="reveal-owner">
          {t('ui.item.formerOwnerLabel')} {text.slice(0, shown)}
        </p>
      )}
      <div class="ht-actions reveal-actions">
        {ready && (
          <TextButton
            id="reveal-take"
            onClick={() => {
              if (it.uniqueId) markClaimed(`reveal.${it.uniqueId}`);
              finishReveal();
            }}
          >
            {t('ui.item.take')}
          </TextButton>
        )}
      </div>
    </section>
  );
}
